import React from 'react';
import { TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { InboundConfiguration } from './inboundConfigurationTypes';

interface InboundConfigurationDeleteModalProps {
  isOpen: boolean;
  config: InboundConfiguration | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const InboundConfigurationDeleteModal: React.FC<InboundConfigurationDeleteModalProps> = ({
  isOpen,
  config,
  deleting,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-slate-900/90 border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Delete Configuration</h3>
            <p className="text-sm text-gray-300 mt-1">This will permanently delete your inbound call configuration.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close modal"
            disabled={deleting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-slate-800/60 border border-white/10 rounded-lg p-4">
            <p className="text-sm text-gray-300 mb-2">Current Configuration</p>
            <div className="space-y-1">
              <p className="text-sm text-white">
                <span className="text-gray-400">Assistant ID:</span> {config?.assistant_id || 'N/A'}
              </p>
              <p className="text-sm text-white">
                <span className="text-gray-400">Status:</span> {config?.is_active ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm text-yellow-100">
            <p className="font-semibold mb-2 text-yellow-200">⚠️ Warning</p>
            <p className="text-yellow-100">This action cannot be undone. You will need to reconfigure your inbound call settings to receive calls.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 bg-white/5 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors border border-white/10"
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${
              deleting ? 'bg-red-900 text-red-200 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {deleting ? (
              <>
                <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                Deleting…
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InboundConfigurationDeleteModal;
